(ns frontend.ui.component.lp-portal.report.body.real-estate.fund.resi-v2.controller
  
  (:require
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.component.lp-portal.donut-chart.view :as donut-chart]
   [frontend.ui.component.lp-portal.report.body.invest-europe.holding.controller :refer [invest-europe-editable-data->raw-data
                                                                                         invest-europe-table-raw-data->editable-data]]
   [frontend.ui.component.lp-portal.report.body.real-estate.fund.common.controller :refer [editable-data->raw-data]]
   [frontend.ui.component.lp-portal.report.component.multi-chart.controller :as multi-controller]
   [frontend.ui.component.lp-portal.report.component.waterfall-chart.controller :as waterfall-chart-ctl]
   [frontend.ui.component.lp-portal.report.controller :as report-ctl]
   [frontend.utils :as fu]))

(defn- fair-value-by-country
  [{:keys [labels country]}]
  (map vector labels country))

(defn- fair-value-by-location
  [{:keys [labels location]}]
  (map vector labels location))

(defn- fair-value-by-type
  [{:keys [labels type]}]
  (map vector labels type))

(defn contact-images
  [{:keys [dynamic-report-images report-data] :as props}]
  (let [column-count (min (count (get-in report-data [:contact-information :column-data-types])) 5)]
    (into {}
          (map (fn [idx]
                 (let [key (keyword (str "contact-" (inc idx)))
                       image-key (keyword (str "contact-image-" (inc idx)))
                       image-value (get dynamic-report-images image-key)]
                   [key (report-ctl/image-content-resi-v2 props image-key image-value)]))
               (range column-count)))))

(defn contact-table-data
  [{:keys [report-data] :as _props}]
  (let [column-data-types-max-5 (take 5 (get-in report-data [:contact-information :column-data-types]))
        new-data (assoc-in report-data [:contact-information :column-data-types] column-data-types-max-5)]
    (:contact-information new-data)))

(defn snapshot-raw-data->editable-data
  [raw-data]
  {:columns ["Label" "Value"]
   :highlight-figures? true
   :data    (->> raw-data
                 :data
                 (mapv (fn [row]
                         (vec (concat [(:row-key row)] (:row-data row))))))
   :cell-data-types (->> raw-data
                         :data
                         (mapv :cell-data-types))
   :cell-styles (->> raw-data
                     :data
                     (mapv :cell-styles))})

(defn- valid-annotation-data?
  [total1 total2]
  (and (number? total1)
       (number? total2)
       (pos? total1)))

(defn- category-total
  [series idx]
  (->> series
       (map #(get-in % [:data idx]))
       (keep #(when (number? %)
                %))
       (reduce + 0)))

(defn like-for-like-annotation
  [data]
  (let [series (get data :series)
        item-count (->> series
                        (map #(count (:data %)))
                        (reduce min))]
    (->> (range (dec item-count))
         (map (fn [idx]
                (let [total1 (category-total series idx)
                      total2 (category-total series (inc idx))]
                  (when (valid-annotation-data? total1 total2)
                    (let [start-x (+ idx 0.25)
                          end-x (+ idx 0.75)
                          mid-x (+ idx 0.5)
                          mid-y (+ total1 (* 0.5 (- total2 total1)))
                          pct-change (* 100 (/ (- total2 total1) total1))]
                      {:shapes [{:type "path"
                                 :points [{:x start-x :y total1 :xAxis 0 :yAxis 0}
                                          {:x end-x :y total2 :xAxis 0 :yAxis 0}]
                                 :stroke "#000000"
                                 :strokeWidth 2
                                 :markerEnd "arrow"}]
                       :labels [{:point {:x mid-x :y mid-y :xAxis 0 :yAxis 0}
                                 :backgroundColor "transparent"
                                 :borderColor "transparent"
                                 :borderWidth 0
                                 :padding 4
                                 :y -30
                                 :style {:color "#000000"
                                         :fontWeight "bold"
                                         :fontSize "16px"}
                                 :text (str (if (pos? pct-change) "+" "")
                                            (.toFixed pct-change 1)
                                            "%")}]})))))
         (keep identity)
         vec
         not-empty)))

(defn ^{:jsdoc ["@nocollapse"]} custom-donut-legend-formatter
  []
  (this-as
   js-data-point
   (let [point (js->clj js-data-point
                        :keywordize-keys
                        true)
         legend-name (fu/aget-in point ["name"])]
     (str legend-name))))

(defn ->ui-data
  [{:keys [_dynamic-data dynamic-report-images report-data] :as props}]
  {:fund-name                                (:fund-name report-data)
   :pdf-cover-image                          (report-ctl/image-content-resi-v2
                                              props
                                              :pdf-cover-image
                                              (:pdf-cover-image
                                               dynamic-report-images))
   :pdf-toc-image                          (report-ctl/image-content-resi-v2
                                              props
                                              :pdf-toc-image
                                              (:pdf-toc-image
                                               dynamic-report-images))
   :reference-date                           (get-in report-data
                                                     [:reference-date])
   :reporting-period                         (:reporting-period
                                              report-data)

   :fund-summary                             (report-ctl/with-footnote
                                               props
                                               :fund-summary-footnotes
                                               (:fund-summary
                                                report-data))
   :contact-images                           (contact-images
                                              props)
   :contact-information                      (report-ctl/with-footnote
                                               props
                                               nil
                                               (contact-table-data props))
   :valuation-details                        (report-ctl/with-footnote
                                               props
                                               :valuation-details-footnotes
                                               (:valuation-details
                                                report-data))
   :key-metrics                              (report-ctl/with-footnote
                                               props
                                               :key-metrics-footnotes
                                               (:key-metrics
                                                report-data))
   :fund-capital-metrics                     (report-ctl/with-footnote
                                               props
                                               :fund-capital-metrics-footnotes
                                               (:fund-capital-metrics report-data)) 
   :fund-kpis-metrics                        (report-ctl/with-footnote
                                               props
                                               :fund-kpis-metrics-footnotes
                                               (:fund-kpis-metrics report-data)) 
   :performance-metrics                      (report-ctl/with-footnote
                                               props
                                               :performance-metrics-footnotes
                                               (:performance-metrics report-data)) 
   :portfolio-metrics                        (report-ctl/with-footnote
                                               props
                                               :portfolio-metrics-footnotes
                                               (:portfolio-metrics report-data))
   :portfolio-snapshot                       (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:portfolio-snapshot]))
                                                  (report-ctl/with-editable-tables
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props)
                                                    :portfolio-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :financial-snapshot                       (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:financial-snapshot]))
                                                  (report-ctl/with-editable-tables
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props)
                                                    :financial-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :esg-snapshot                             (->> (report-ctl/with-footnote props
                                                    nil
                                                    (get-in report-data [:esg-snapshot]))
                                                  (report-ctl/with-editable-tables 
                                                    (merge
                                                     {:fixed-columns? true
                                                      :fixed-labels? true
                                                      :fixed-rows? true} props) 
                                                    :esg-snapshot
                                                    snapshot-raw-data->editable-data
                                                    editable-data->raw-data))
   :quarter-update-image-1                   (report-ctl/image-content-resi-v2
                                              props
                                              :quarter-update-image-1
                                              (:quarter-update-image-1
                                               dynamic-report-images))
   :quarter-update-image-2                   (report-ctl/image-content-resi-v2
                                              props
                                              :quarter-update-image-2
                                              (:quarter-update-image-2
                                               dynamic-report-images))
   :market-overview                          (->> (report-ctl/with-footnote
                                                    props
                                                    :market-overview-footnotes
                                                    (:market-overview report-data))
                                                  (report-ctl/with-editable-tables 
                                                    (merge {:fixed-rows? true} props)
                                                    :market-overview
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :market-overview-image                    (report-ctl/image-content-resi-v2
                                              props
                                              :market-overview-image
                                              (:market-overview-image
                                               dynamic-report-images))
   :fund-returns-table                       (report-ctl/with-footnote
                                               props
                                               :fund-returns-table-footnotes
                                               (:fund-returns-table
                                                report-data))
   :benchmark-index                          (report-ctl/with-footnote
                                               props
                                               :benchmark-index-footnotes
                                               (:benchmark-index
                                                report-data))
   :nav-per-unit                             (report-ctl/with-footnote
                                               props
                                               :nav-per-unit-footnotes
                                               (multi-controller/multi-chart-ctl-capman
                                                (:nav-per-unit
                                                 report-data)))
   :nav-bridge                               (report-ctl/with-footnote
                                               props
                                               :nav-bridge-footnotes
                                               (waterfall-chart-ctl/chart-config-capman
                                                (:nav-bridge
                                                 report-data)
                                                report-data))
   :debt-kpis                                (report-ctl/with-footnote
                                               props
                                               :debt-kpis-footnotes
                                               (:debt-kpis
                                                report-data))
   :debt-facilities                          (report-ctl/with-footnote
                                               props
                                               :debt-facilities-footnotes
                                               (:debt-facilities
                                                report-data))
   :loan-to-gav                              (report-ctl/with-footnote
                                               props
                                               :loan-to-gav-footnotes
                                               (multi-controller/multi-chart-ctl-capman
                                                (:loan-to-gav
                                                 report-data)
                                                {:custom-y-axis-formatter chart-utils/axis-pct-formatter-0-decimals}))
   :debt-maturity                            (report-ctl/with-footnote
                                               props
                                               nil
                                               (multi-controller/multi-chart-ctl-capman
                                                (:debt-maturity
                                                 report-data)))
   :portfolio-summary                        (report-ctl/with-footnote
                                               props
                                               :portfolio-summary-footnotes
                                               (:portfolio-summary
                                                report-data))
   :portfolio-noi-and-niy                    (report-ctl/with-footnote
                                               props
                                               nil
                                               (multi-controller/multi-chart-ctl-capman
                                                (:portfolio-noi-and-niy
                                                 report-data)))
   :like-for-like-rental-growth              (report-ctl/with-footnote
                                               props
                                               nil
                                               (multi-controller/multi-chart-ctl-capman
                                               (:like-for-like-rental-growth
                                                report-data)
                                               {:stack-total-label? true
                                                :top-stack-only-label? true
                                                 :stack-total-decimal-amount 1
                                                 :custom-annotation (like-for-like-annotation
                                                                     (:like-for-like-rental-growth
                                                                      report-data))}))
   :valuation-change                         (report-ctl/with-footnote
                                               props
                                               :valuation-change-footnotes
                                               (waterfall-chart-ctl/chart-config-capman
                                                (:valuation-change
                                                 report-data)
                                                report-data))
   :fair-value-by-country                    (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-country
                                                 (:chart-data-fair-value-by-country
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter}))
   :fair-value-by-location                   (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-location
                                                 (:chart-data-fair-value-by-location
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter}))
   :fair-value-by-type                       (report-ctl/with-footnote
                                               props
                                               nil
                                               (donut-chart/donut-chart-config
                                                (fair-value-by-type
                                                 (:chart-data-fair-value-by-type
                                                  report-data))
                                                {:data-label-formatter
                                                 chart-utils/pie-%-label-formatter-no-letters-0-decimals
                                                 :resi-custom-colors? true
                                                 :legend-formatter-fn custom-donut-legend-formatter}))
   :acquisitions                             (report-ctl/with-footnote
                                               props
                                               :acquisitions-footnotes
                                               (:acquisitions
                                                report-data))
   :dispositions                             (report-ctl/with-footnote
                                               props
                                               :dispositions-footnotes
                                               (:dispositions
                                                report-data))
   :investment-activity-image-1              (report-ctl/image-content-resi-v2
                                              props
                                              :investment-activity-image-1
                                              (:investment-activity-image-1
                                               dynamic-report-images))
   :investment-activity-image-2              (report-ctl/image-content-resi-v2
                                              props
                                              :investment-activity-image-2
                                              (:investment-activity-image-2
                                               dynamic-report-images))
   :gresb-by-year                            (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:gresb-by-year report-data))
                                                  (report-ctl/with-editable-tables props :gresb-by-year
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :gresb-logo-image                         (report-ctl/image-content-resi-v2
                                              props
                                              :gresb-logo-image
                                              (:gresb-logo-image
                                               dynamic-report-images))
   :portfolio-epc                            (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:portfolio-epc report-data))
                                                  (report-ctl/with-editable-tables props :portfolio-epc
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :green-certs-by-country                   (->> (report-ctl/with-footnote
                                                    props
                                                    :green-certs-by-country-footnote
                                                    (:green-certs-by-country report-data))
                                                  (report-ctl/with-editable-tables props :green-certs-by-country
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :green-certificate-image-1                (report-ctl/image-content-resi-v2
                                              props
                                              :green-certificate-image-1
                                              (:green-certificate-image-1 dynamic-report-images))
   :green-certificate-image-2                (report-ctl/image-content-resi-v2
                                              props
                                              :green-certificate-image-2
                                              (:green-certificate-image-2 dynamic-report-images))
   :esg-investments-image                    (report-ctl/image-content-resi-v2
                                              props
                                              :esg-investments-image
                                              (:esg-investments-image
                                               dynamic-report-images))
   :share-of-fossil-free-energy              (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:share-of-fossil-free-energy report-data))
                                                  (report-ctl/with-editable-tables props :share-of-fossil-free-energy
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :emissions                                (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:emissions report-data))
                                                  (report-ctl/with-editable-tables props :emissions
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :annual-carbon-intensity                   (->>
                                               (get-in report-data
                                                       [:annual-carbon-intensity])
                                               (report-ctl/with-footnote
                                                 props
                                                 nil)
                                               (report-ctl/with-editable-charts
                                                 (merge {:multi-chart? true} props)
                                                 :annual-carbon-intensity
                                                 multi-controller/multi-chart-ctl-capman
                                                 multi-controller/config->editable-data
                                                 multi-controller/editable-data->raw-data))
   
   :cmrf-towards-net-zero                    (->>
                                              (get-in report-data
                                                      [:cmrf-towards-net-zero])
                                              (report-ctl/with-footnote
                                                props
                                                nil)
                                              (report-ctl/with-editable-charts
                                                (merge {:multi-chart? true} props)
                                                :cmrf-towards-net-zero
                                                multi-controller/multi-chart-ctl-capman
                                                multi-controller/config->editable-data
                                                multi-controller/editable-data->raw-data))
   :esg-nps                                  (->> (report-ctl/with-footnote
                                                    props
                                                    nil
                                                    (:esg-nps report-data))
                                                  (report-ctl/with-editable-tables props :esg-nps
                                                    invest-europe-table-raw-data->editable-data
                                                    invest-europe-editable-data->raw-data))
   :crrem-logo-image                         (report-ctl/image-content-resi-v2
                                              props
                                              :crrem-logo-image
                                              (:crrem-logo-image
                                               dynamic-report-images))
   :nav-breakdown-ifrs                       (report-ctl/with-footnote
                                               props
                                               :nav-breakdown-ifrs-footnotes
                                               (:nav-breakdown-ifrs
                                                report-data))
   :nav-breakdown-inrev                      (report-ctl/with-footnote
                                               props
                                               :nav-breakdown-inrev-footnotes
                                               (:nav-breakdown-inrev
                                                report-data))
   :income-statement                         (report-ctl/with-footnote
                                               props
                                               :income-statement-footnotes
                                               (:income-statement
                                                report-data))
   :financial-positions-statement-1          (report-ctl/with-footnote
                                               props
                                               :financial-positions-statement-1-footnotes
                                               (:financial-positions-statement-1
                                                report-data))
   :financial-positions-statement-2          (report-ctl/with-footnote
                                               props
                                               :financial-positions-statement-2-footnotes
                                               (:financial-positions-statement-2
                                                report-data))
   :cash-flows-statement-1                   (report-ctl/with-footnote
                                               props
                                               :cash-flows-statement-1-footnotes
                                               (:cash-flows-statement-1
                                                report-data))
   :cash-flows-statement-2                   (report-ctl/with-footnote
                                               props
                                               :cash-flows-statement-2-footnotes
                                               (:cash-flows-statement-2
                                                report-data))
   :portfolio-denmark                        (report-ctl/with-footnote
                                               props
                                               :portfolio-denmark-footnotes
                                               (:portfolio-denmark
                                                report-data))
   :portfolio-finland                        (report-ctl/with-footnote
                                               props
                                               :portfolio-finland-footnotes
                                               (:portfolio-finland
                                                report-data))
   :portfolio-sweden                         (report-ctl/with-footnote
                                               props
                                               :portfolio-sweden-footnotes
                                               (:portfolio-sweden
                                                report-data))
   :glossary                                 (report-ctl/with-footnote
                                               props
                                               :glossary-footnotes
                                               (:glossary
                                                report-data))

   ;; --- Rich content blocks ---
   :strategy                                 (report-ctl/rich-content
                                              props
                                              :strategy)
   :portfolio-update-text                    (report-ctl/rich-content
                                              props
                                              :portfolio-update-text)
   :investment-activity-and-pipeline-text    (report-ctl/rich-content
                                              props
                                              :investment-activity-and-pipeline-text)
   :performance-and-outlook-text             (report-ctl/rich-content
                                              props
                                              :performance-and-outlook-text)
   :esg-summary-text                         (report-ctl/rich-content
                                              props
                                              :esg-summary-text)
   :gresb-text                               (report-ctl/rich-content
                                              props
                                              :gresb-text)
   :epc-text                                 (report-ctl/rich-content
                                              props
                                              :epc-text)
   :eu-taxonomy-text                         (report-ctl/rich-content
                                              props
                                              :eu-taxonomy-text)
   :green-certificates-text                  (report-ctl/rich-content
                                              props
                                              :green-certificates-text)
   :green-electricity-text                   (report-ctl/rich-content
                                              props
                                              :green-electricity-text)
   :esg-investments-text                     (report-ctl/rich-content
                                              props
                                              :esg-investments-text)
   :crrem-pathway-text                       (report-ctl/rich-content
                                              props
                                              :crrem-pathway-text)
   :tenant-satisfaction-text                 (report-ctl/rich-content
                                              props
                                              :tenant-satisfaction-text)
   :fees-disclosure-text                     (report-ctl/rich-content
                                              props
                                              :fees-disclosure-text)
   :disclaimer-text                          (report-ctl/rich-content
                                              props
                                              :disclaimer-text)})
